import {
  Color,
  Point,
  Shape,
  SVG,
  Text,
  Element,
  Container,
} from "@svgdotjs/svg.js";
import * as fmin from "fmin";
import { parse, format } from "footinch";

let EDGE_NAMES = ["ab", "bc", "cd", "da", "ac", "bd"];
let INPUTS = Array<HTMLInputElement>();
let INPUT_VALUES = Array<number>();
let RESOLVEDS = Array<HTMLInputElement>();
let RESOLVED_VALUES = Array<number>();
let ERRS = Array<HTMLInputElement>();
let DXS = Array<HTMLInputElement>();
let DYS = Array<HTMLInputElement>();
let draw = null;
let CANVAS_SIZE = 300;
let PADDING = 20;
let DRAW_SCALE = 1;
let ANGLE_ACCURACY_REQ = 0.01;
let INPUT_SIZE = 10;
let FONT_SIZE = 10;
// let PREFER_CHANGE_DIAGS = true
// let PREFER_SHRINK = true
// let FUDGE_PENALTY = 10

// In metric, number of decimal places
// In imperial, 0 = 1", 1 = 1/2", 2 = 1/4", 3 = 1/8", etc.
let PRECISION = 4;

enum UNIT {
  METRIC,
  IMPERIAL,
}

let units = UNIT.METRIC;

// Visual config
let topLeftIdx: number; // 0 = A in top left, 1 = B, 2 = C, 3 = D
let drawHorz: boolean; // If true, the reference edge is horizontal. Otherwise vertical.
let flip: boolean; // If true, draw A D C B instead of A B C D

// One-liner lambdas
let toDeg = (rad) => {
  return (rad * 180) / Math.PI;
};
let absErrSum = (acc, err) => {
  return acc + Math.abs(err);
};
let hasNaN = (v) => {
  return isNaN(v);
};

function onInput(ev) {
  resolveInputs();
}

function addInput(name) {
  let row = document.createElement("tr");

  let tdLabel = document.createElement("td");
  let label = document.createElement("label");

  let tdInput = document.createElement("td");
  let input = document.createElement("input");

  let tdResolved = document.createElement("td");
  let resolved = document.createElement("input");

  let tdErr = document.createElement("td");
  let err = document.createElement("input");

  let tdDx = document.createElement("td");
  let dx = document.createElement("input");

  let tdDy = document.createElement("td");
  let dy = document.createElement("input");

  label.setAttribute("for", name);
  label.innerText = name.toUpperCase();

  input.name = name;
  input.id = name;
  input.size = INPUT_SIZE;

  input.addEventListener("input", onInput);
  INPUTS.push(input);

  resolved.size = INPUT_SIZE;
  resolved.disabled = true;
  RESOLVEDS.push(resolved);

  err.size = INPUT_SIZE;
  err.disabled = true;
  ERRS.push(err);

  dx.size = INPUT_SIZE;
  dx.disabled = true;
  DXS.push(dx);

  dy.size = INPUT_SIZE;
  dy.disabled = true;
  DYS.push(dy);

  tdLabel.appendChild(label);
  tdInput.appendChild(input);
  tdResolved.appendChild(resolved);
  tdErr.appendChild(err);
  tdDx.appendChild(dx);
  tdDy.appendChild(dy);

  row.appendChild(tdLabel);
  row.appendChild(tdInput);
  row.appendChild(tdResolved);
  row.appendChild(tdErr);
  row.appendChild(tdDx);
  row.appendChild(tdDy);

  return row;
}

function collectInputValues() {
  const parser = units == UNIT.IMPERIAL ? parse.F : parseFloat;
  INPUT_VALUES = INPUTS.map((input) => parser(input.value));
  return [...INPUT_VALUES];
}

function collectResolvedValues() {
  RESOLVED_VALUES = RESOLVEDS.map((input) =>
    parseFloat(input.dataset.floatStr)
  );
  return RESOLVED_VALUES;
}

function measurementError(guess) {
  let errs = [];

  // If we prefer shrinking and we have to grow, penalize harder
  // If we prefer growing and we have to shirk, penalize harder
  for (let i = 0; i < guess.length; ++i) {
    let err = (guess[i] - INPUT_VALUES[i]) / INPUT_VALUES[i];

    // // Diagonals
    // if (i >= 4) {
    //   // Fudge error if we're "on the diagonal"
    //   // They're more likely to be wrong because they're not near 90 degrees
    //   if (PREFER_CHANGE_DIAGS)
    //     err /= FUDGE_PENALTY
    // }
    // // Primaries
    // else {
    //   // Fudge error if we're adjusting in the "wrong direction"
    //   if (err > 0 && PREFER_SHRINK) {
    //     err *= FUDGE_PENALTY
    //   } else if (err < 0 && !PREFER_SHRINK) {
    //     err *= FUDGE_PENALTY
    //   }
    // }

    errs.push(err);
  }

  return errs.reduce(absErrSum, 0);
}

// Return angle OPPOSITE the first side
function getAngle(a, b, c) {
  return Math.acos((b * b + c * c - a * a) / (2 * b * c));
}

// Return all angles from a triangle
function getAllAngles(a, b, c) {
  return [getAngle(a, b, c), getAngle(b, c, a), getAngle(c, a, b)];
}

function getTwoDiagEstimates([a, b, c, d, e, f]) {
  return [].concat(
    getAllAngles(a, b, e),
    getAllAngles(b, c, f),
    getAllAngles(c, d, e),
    getAllAngles(d, a, f)
  );
}

function angleError(guess) {
  const [BE, AE, AB, CF, BF, BC, DE, CE, CD, AF, DF, AD] = getTwoDiagEstimates(
    guess
  );

  const ABp = AF + BF;
  const BCp = BE + CE;
  const CDp = CF + DF;
  const ADp = AE + DE;

  const errors = [ABp - AB, BCp - BC, CDp - CD, ADp - AD];

  const error = errors.reduce(absErrSum, 0);
  // console.log(error)
  return error;
}

function costFunction(guess) {
  return angleError(guess) + measurementError(guess);
}

function getTrianglePoints(
  initialAngle: number,
  AB: number,
  BC: number,
  AC: number,
  scale: number
): Array<Point> {
  let ABC = getAngle(AC, AB, BC);
  let BCA = getAngle(AB, BC, AC);
  let CAB = getAngle(BC, AB, AC);

  let angle = initialAngle;

  let A = new Point(0, 0);

  let B = new Point(
    A.x + AB * scale * Math.cos(angle),
    A.y + AB * scale * Math.sin(angle)
  );

  if (drawHorz) {
    angle += Math.PI - ABC;
  } else {
    angle -= Math.PI - ABC;
  }

  let C = new Point(
    B.x + BC * scale * Math.cos(angle),
    B.y + BC * scale * Math.sin(angle)
  );

  return [A, B, C];
}

function addPoints(a: Point, b: Point): Point {
  return new Point(a.x + b.x, a.y + b.y);
}

function drawTriangle(
  points: Array<Point>,
  offset: Point,
  stroke: Object
): Array<Point> {
  let [A, B, C] = points;

  let oA = addPoints(offset, A);
  let oB = addPoints(offset, B);
  let oC = addPoints(offset, C);

  draw.line(oA.x, oA.y, oB.x, oB.y).stroke(stroke);
  draw.line(oB.x, oB.y, oC.x, oC.y).stroke(stroke);
  draw.line(oC.x, oC.y, oA.x, oA.y).stroke(stroke);

  return [oA, oB, oC];
}

function calcInitialAngle(): number {
  return drawHorz ? 0 : Math.PI / 2;
}

function drawQuadrilateral(
  sideLengths: Array<number>,
  offset: Point,
  stroke: Object
): Array<Point> {
  let AB, BC, CD, DA, AC, BD;
  [AB, BC, CD, DA, AC, BD] = sideLengths;

  // Draw three triangles (all the ones that touch our base point at 0,0)
  // ABC, ACD, ABD
  let [A, B, C] = getTrianglePoints(calcInitialAngle(), AB, BC, AC, DRAW_SCALE);
  drawTriangle([A, B, C], offset, stroke);

  let angle = calcInitialAngle();
  if (drawHorz) {
    angle += getAngle(BC, AB, AC);
  } else {
    angle -= getAngle(BC, AB, AC);
  }
  let [, , D] = getTrianglePoints(angle, AC, CD, DA, DRAW_SCALE);
  drawTriangle(
    getTrianglePoints(angle, AC, CD, DA, DRAW_SCALE),
    offset,
    stroke
  );

  drawTriangle(
    getTrianglePoints(calcInitialAngle(), AB, BD, DA, DRAW_SCALE),
    offset,
    stroke
  );

  return [A, B, C, D];
}

function reorderedIdx(i: number, startIdx: number) {
  if (flip) {
    return (((3 + startIdx) % 4) - i + 4) % 4;
  } else {
    return (i + startIdx) % 4;
  }
}

function reorderSides(
  sideLengths: Array<number>,
  startIdx: number
): Array<number> {
  let reordered = new Array<number>(sideLengths.length);

  for (let i = 0; i < 4; ++i) {
    reordered[i] = sideLengths[reorderedIdx(i, startIdx)];
  }

  // With A or C first, the first diagonal should be "E"
  if (topLeftIdx == 0 || topLeftIdx == 2) {
    reordered[4] = sideLengths[4];
    reordered[5] = sideLengths[5];
  }
  // With B or D first, the first diagonal should be "F"
  else {
    reordered[4] = sideLengths[5];
    reordered[5] = sideLengths[4];
  }

  return reordered;
}

function labelIdx(i: number, startIdx: number) {
  return flip ? (startIdx - i + 4) % 4 : (i + startIdx) % 4;
}

function averagePoints(a: Point, b: Point): Point {
  return new Point((a.x + b.x) / 2, (a.y + b.y) / 2);
}

// Only works on a well-defined quadrilateral (i.e. the "resolved" as opposed to "input")
function getQuadrilateralPoints(sideLengths: Array<number>): Array<Point> {
  let AB, BC, CD, DA, AC, BD;
  [AB, BC, CD, DA, AC, BD] = sideLengths;

  let [A, B, C] = getTrianglePoints(calcInitialAngle(), AB, BC, AC, DRAW_SCALE);

  let angle = calcInitialAngle();
  if (drawHorz) {
    angle += getAngle(BC, AB, AC);
  } else {
    angle -= getAngle(BC, AB, AC);
  }
  let [, , D] = getTrianglePoints(angle, AC, CD, DA, DRAW_SCALE);

  return [A, B, C, D];
}

// Find how much in X and Y a quadrilateral (defined by side lengths)
// would have to be adjusted in order for it not to "go negative"
// Stops the SVG from clipping out of bounds
function getOffset(sideLengths: Array<number>): Point {
  const pts = getQuadrilateralPoints(sideLengths);

  let minX = pts.reduce((min, val) => {
    return val.x < min ? val.x : min;
  }, Number.MAX_VALUE);
  let minY = pts.reduce((min, val) => {
    return val.y < min ? val.y : min;
  }, Number.MAX_VALUE);

  // Result should be amount required to shift to make it "positive"
  minX = Math.max(0, -minX);
  minY = Math.max(0, -minY);

  return new Point(minX, minY);
}

function fillBehind(element: Shape): Element {
  const bbox = element.bbox();
  return draw
    .rect(bbox.w, bbox.h)
    .fill({ color: "#fff", opacity: 0.75 })
    .move(bbox.x, bbox.y)
    .insertBefore(element)
    .css("pointer-events", "none");
}

function formatter(): (number) => string {
  if (units == UNIT.IMPERIAL) {
    return format.FT.to.FT.IN.FRAC(Math.pow(2, PRECISION));
  } else {
    return (f) => {
      return f.toFixed(PRECISION);
    };
  }
}

function len(p: Point): number {
  return Math.sqrt(p.x * p.x + p.y * p.y);
}

function diffPoints(p1: Point, p2: Point): Point {
  return new Point(p2.x - p1.x, p2.y - p1.y);
}

let exclusiveElems = [];
let elemToLabel = new Map<Element, Element>();

function addExclusive(hover: Element, text: Container) {
  exclusiveElems.push(text);

  // On mouseover, hide all other exclusives and show this one
  hover.mouseover(() => {
    exclusiveElems.map((elem) => {
      elem == text ? elem.show() : elem.hide();
    });
  });
  hover.mouseout(() => {
    text.hide();
  });

  // Initially hide
  text.hide();
}

function redraw() {
  draw.clear();
  draw.viewbox(
    -PADDING,
    -PADDING,
    CANVAS_SIZE + 2 * PADDING,
    CANVAS_SIZE + 2 * PADDING
  );

  collectInputValues();
  collectResolvedValues();

  // Get a draw scale to fill the viewbox
  DRAW_SCALE = 1;
  const unscaledPts = getQuadrilateralPoints(
    reorderSides(RESOLVED_VALUES, topLeftIdx)
  );
  let minX = Number.MAX_VALUE;
  let maxX = Number.MIN_VALUE;
  let minY = Number.MAX_VALUE;
  let maxY = Number.MIN_VALUE;
  unscaledPts.map((pt) => {
    minX = Math.min(minX, pt.x);
    maxX = Math.max(maxX, pt.x);
    minY = Math.min(minY, pt.y);
    maxY = Math.max(maxY, pt.y);
  });

  const dx = maxX - minX;
  const dy = maxY - minY;

  // Get the "offset" of the resolved quadrilateral
  // We'll adjust everything by that amount
  let offset = getOffset(reorderSides(RESOLVED_VALUES, topLeftIdx));

  const maxDim = Math.max(dx + offset.x, dy + offset.y);

  DRAW_SCALE = CANVAS_SIZE / maxDim;
  offset.x *= DRAW_SCALE;
  offset.y *= DRAW_SCALE;

  // Draw the (possibly inaccurate) input quadrilateral
  // drawQuadrilateral(
  //   reorderSides(INPUT_VALUES, topLeftIdx),
  //   offset,
  //   { color: '#ffcdd2', width: 5, linecap: 'round' }
  // )

  // Draw the (optimized) resolved quadrilateral
  let corners = drawQuadrilateral(
    reorderSides(RESOLVED_VALUES, topLeftIdx),
    offset,
    { color: "#777", width: 1, linecap: "round" }
  );

  // Label the corner points
  const cornerLabels = ["A", "B", "C", "D"];
  for (let i = 0; i < 4; ++i) {
    const label = cornerLabels[labelIdx(i, topLeftIdx)];
    const elem = draw
      .text(label)
      .move(offset.x + corners[i].x, offset.y + corners[i].y);
    fillBehind(elem);
  }

  // Draw the X/Y helpers for each side
  let pts = getQuadrilateralPoints(reorderSides(RESOLVED_VALUES, topLeftIdx));

  const dashStroke = {
    color: "#000",
    dasharray: "3, 3",
    width: 1,
    opacity: 0.7,
  };
  const hoverStroke = { color: "#0f0", width: 10, opacity: 0.0 };

  // Write delta values into the table
  for (let i = 0; i < 4; ++i) {
    for (let j = i + 1; j < 4; ++j) {
      const dx = Math.abs(pts[j].x - pts[i].x);
      const dy = Math.abs(pts[j].y - pts[i].y);

      // Determine index of table row to write into
      // 0 = AB, 1 = BC, 2 = CD, 3 = DA, 4 = AC, 5 = BD
      // 0 -> 0 & 1, 1 -> 1 & 2, 2 -> 2 & 3, 3 -> 0 & 3, 4 => 0 & 2, 5 => 1 & 3
      // But this changes with startIdx!
      const idxI = labelIdx(i, topLeftIdx);
      const idxJ = labelIdx(j, topLeftIdx);

      let minIdx = Math.min(idxI, idxJ);
      let maxIdx = Math.max(idxI, idxJ);

      let idx;
      switch (minIdx) {
        case 0: {
          switch (maxIdx) {
            case 1: {
              idx = 0;
              break;
            }
            case 2: {
              idx = 4;
              break;
            }
            case 3: {
              idx = 3;
              break;
            }
          }
          break;
        }
        case 1: {
          idx = maxIdx == 2 ? 1 : 5;
          break;
        }
        case 2: {
          idx = minIdx;
          break;
        }
        case 3: {
          idx = minIdx;
          break;
        }
      }

      // console.log(
      //   i,
      //   j,
      //   dx / DRAW_SCALE,
      //   dy / DRAW_SCALE,
      //   idxI,
      //   idxJ,
      //   minIdx,
      //   maxIdx,
      //   idx
      // );

      DXS[idx].value = formatter()(dx / DRAW_SCALE);
      DYS[idx].value = formatter()(dy / DRAW_SCALE);
    }
  }

  // Start at 1 -- looking at *pairs* of corner points
  // Index 0 is the "top left" point always
  // Index 1 can be next point either clockwise or counter-clockwise (depending on drawHorz)
  // Don't draw from 0 to 1 because that's been locked to either horizontal or vertical
  for (let i = 1; i < 4; ++i) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % 4];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    // The root can either be p1.x and p2.y or p2.x and p1.y
    // Draw a line from the root to each of the other points
    // The root should always be "on the outside" of the quadrilateral
    let root: Point;
    if (dx > 0) {
      if (dy > 0) {
        root = drawHorz ? new Point(p2.x, p1.y) : new Point(p1.x, p2.y);
      } else {
        root = drawHorz ? new Point(p1.x, p2.y) : new Point(p2.x, p1.y);
      }
    } else {
      if (dy > 0) {
        root = drawHorz ? new Point(p1.x, p2.y) : new Point(p2.x, p1.y);
      } else {
        root = drawHorz ? new Point(p2.x, p1.y) : new Point(p1.x, p2.y);
      }
    }

    draw
      .line(
        offset.x + root.x,
        offset.y + root.y,
        offset.x + p1.x,
        offset.y + p1.y
      )
      .stroke(dashStroke);
    const hover1 = draw
      .line(
        offset.x + root.x,
        offset.y + root.y,
        offset.x + p1.x,
        offset.y + p1.y
      )
      .stroke(hoverStroke);
    draw
      .line(
        offset.x + root.x,
        offset.y + root.y,
        offset.x + p2.x,
        offset.y + p2.y
      )
      .stroke(dashStroke);
    const hover2 = draw
      .line(
        offset.x + root.x,
        offset.y + root.y,
        offset.x + p2.x,
        offset.y + p2.y
      )
      .stroke(hoverStroke);

    // Draw length labels on helpers
    const text1 = drawHelperText(
      addPoints(offset, p1),
      addPoints(offset, root)
    );
    const text2 = drawHelperText(
      addPoints(offset, p2),
      addPoints(offset, root)
    );

    addExclusive(hover1, text1);
    addExclusive(hover2, text2);
  }

  // Draw length labels on primary sides
  for (let i = 0; i < 4; ++i) {
    const hover = draw
      .line(
        offset.x + corners[i].x,
        offset.y + corners[i].y,
        offset.x + corners[(i + 1) % 4].x,
        offset.y + corners[(i + 1) % 4].y
      )
      .stroke(hoverStroke);
    addExclusive(
      hover,
      drawHelperText(
        addPoints(offset, corners[i]),
        addPoints(offset, corners[(i + 1) % 4])
      )
    );
  }

  // Draw the "cross" sides
  {
    const hover = draw
      .line(
        offset.x + corners[0].x,
        offset.y + corners[0].y,
        offset.x + corners[2].x,
        offset.y + corners[2].y
      )
      .stroke(hoverStroke);
    addExclusive(
      hover,
      drawHelperText(
        addPoints(offset, corners[0]),
        addPoints(offset, corners[2])
      )
    );
  }
  {
    const hover = draw
      .line(
        offset.x + corners[1].x,
        offset.y + corners[1].y,
        offset.x + corners[3].x,
        offset.y + corners[3].y
      )
      .stroke(hoverStroke);
    addExclusive(
      hover,
      drawHelperText(
        addPoints(offset, corners[1]),
        addPoints(offset, corners[3])
      )
    );
  }
}

function drawHelperText(p1: Point, p2: Point): Container {
  const helperPt = averagePoints(p1, p2);
  // draw.circle(10).move(helperPt.x-5, helperPt.y-5).fill({ color: '#f00' })
  const group = draw.group();
  const elem = draw
    .text(formatter()(len(diffPoints(p2, p1)) / DRAW_SCALE))
    .font({ size: FONT_SIZE });
  const vAdj = elem.bbox().h / 2;
  elem
    .move(helperPt.x, helperPt.y - vAdj)
    .font({ anchor: "middle" })
    .css("pointer-events", "none");
  const bg = fillBehind(elem);
  group.add(bg);
  group.add(elem);
  return group;
}

function drawError(message: string) {
  draw.clear();
  document.getElementById("error").textContent = message;
}

// Collect all the input values and get the "resolved" versions that actually make a perfect quadrilateral
function resolveInputs() {
  units = (document.getElementById("units") as HTMLSelectElement).selectedIndex;
  topLeftIdx = (document.getElementById("topLeft") as HTMLSelectElement)
    .selectedIndex;
  drawHorz = (document.getElementById("drawHorz") as HTMLInputElement).checked;
  flip = (document.getElementById("flip") as HTMLInputElement).checked;

  // console.log('Running optimization...')

  let result = fmin.nelderMead(costFunction, collectInputValues()); // Some pathological cases still come back as "solved" but they're impossible if (isNaN(result.x) || (!isNaN(result.fx) && angleError(result.x) > ANGLE_ACCURACY_REQ)) {
  // console.log(result)

  if (INPUT_VALUES.some(hasNaN)) {
    drawError("Missing or invalid measurements. Please double-check values.");
    return;
  }

  if (
    isNaN(result.fx) ||
    (!isNaN(result.fx) && angleError(result.x) > ANGLE_ACCURACY_REQ)
  ) {
    drawError(
      "Unable to fit a quadrilateral given these lengths. Please double-check measurements."
    );
    return;
  }

  // No error!
  drawError(null);

  result.x.map((resolved, idx) => {
    RESOLVEDS[idx].value = formatter()(resolved);
    RESOLVEDS[idx].dataset.floatStr = resolved;
    ERRS[idx].value = formatter()(INPUT_VALUES[idx] - resolved);
  });

  redraw();
}

function randomizeInputs() {
  INPUTS.map((input) => {
    input.value = formatter()(Math.random() + 1).toString();
  });
}

function getSaves() {
  return JSON.parse(window.localStorage.getItem("saves")) || {};
}

function refreshLoadList() {
  const loadList = document.getElementById("loadList");
  loadList.innerHTML = null;

  const saves = getSaves();
  const saveNames = [];
  for (const name in saves) {
    saveNames.push(name);
  }
  saveNames.sort((a: string, b: string) => a.localeCompare(b));

  // Add a blank option to go to when we save a new one
  loadList.appendChild(document.createElement("option"));

  saveNames.map((name) => {
    let loadOption = document.createElement("option");
    loadOption.textContent = name;
    loadOption.value = JSON.stringify(saves[name]);
    loadList.appendChild(loadOption);
  });
}

function drawExample() {
  let ex = SVG().addTo("#example").size("100%", "100%");

  let lineStroke = { color: "black", width: 1, linecap: "round" };
  let dashStroke = { color: "gray", width: 1, dasharray: "3, 3" };

  ex.viewbox(-1, -1, 102, 102);

  ex.text("A").move(0, 0).font({ size: 8 });
  ex.text("B").move(85, 0).font({ size: 8 });
  ex.text("C").move(90, 80).font({ size: 8 });
  ex.text("D").move(10, 70).font({ size: 8 });

  ex.line(10, 10, 80, 10).stroke(lineStroke);
  ex.line(80, 10, 90, 80).stroke(lineStroke);
  ex.line(90, 80, 20, 70).stroke(lineStroke);
  ex.line(20, 70, 10, 10).stroke(lineStroke);

  ex.line(10, 10, 90, 80).stroke(dashStroke);
  ex.line(80, 10, 20, 70).stroke(dashStroke);
}

// Add the DOM elements to change inputs
window.addEventListener("load", () => {
  const table = document.getElementById("inputTable");

  EDGE_NAMES.map((name) => {
    table.appendChild(addInput(name));
  });

  if (true) {
    randomizeInputs();
  } else {
    // ['1', '1.5', '2', '2.5', '2', '3'].map((val, idx) => { INPUTS[idx].value = val })
    // ['10', '10', '10', '10', '15', '15'].map((val, idx) => { INPUTS[idx].value = val })
    // ['1.9', '1.9', '1.9', '1.9', '3', '3'].map((val, idx) => { INPUTS[idx].value = val })
    ['40 3/8"', '74 1/2"', '34"', '96 5/8"', '101 3/8"', '81 29/32"'].map(
      (val, idx) => {
        INPUTS[idx].value = val;
      }
    );
  }

  // Changing units
  document.getElementById("units").addEventListener("input", (ev) => {
    units = (ev.target as HTMLSelectElement).selectedIndex;
    resolveInputs();
  });

  // Changing "Top Left" point
  document.getElementById("topLeft").addEventListener("input", (ev) => {
    topLeftIdx = (ev.target as HTMLSelectElement).selectedIndex;
    redraw();
  });

  // Changing "Draw Horizontal"
  document.getElementById("drawHorz").addEventListener("input", (ev) => {
    drawHorz = (ev.target as HTMLInputElement).checked;
    redraw();
  });

  // Changing "Flip"
  document.getElementById("flip").addEventListener("input", (ev) => {
    flip = (ev.target as HTMLInputElement).checked;
    redraw();
  });

  // Randomize button
  document.getElementById("randomize").addEventListener("click", () => {
    randomizeInputs();
    resolveInputs();
  });

  // Clear button
  document.getElementById("clear").addEventListener("click", () => {
    INPUTS.map((input) => {
      input.value = null;
    });
    RESOLVEDS.map((input) => {
      input.value = null;
    });
    ERRS.map((input) => {
      input.value = null;
    });
    DXS.map((input) => {
      input.value = null;
    });
    DYS.map((input) => {
      input.value = null;
    });
    resolveInputs();
    INPUTS[0].focus();
  });

  // Save button
  document.getElementById("save").addEventListener("click", () => {
    let saveNameInput = document.getElementById("saveName") as HTMLInputElement;
    let saveName = saveNameInput.value;
    if (saveName.length <= 0) {
      saveName = "Untitled";
    }
    let saves = getSaves();
    saves[saveName] = {};
    saves[saveName].units = (document.getElementById(
      "units"
    ) as HTMLSelectElement).selectedIndex;
    saves[saveName].topLeft = (document.getElementById(
      "topLeft"
    ) as HTMLSelectElement).selectedIndex;
    saves[saveName].drawHorz = (document.getElementById(
      "drawHorz"
    ) as HTMLInputElement).checked;
    saves[saveName].flip = (document.getElementById(
      "flip"
    ) as HTMLInputElement).checked;
    saves[saveName].values = INPUTS.map((input) => {
      return input.value;
    });
    window.localStorage.setItem("saves", JSON.stringify(saves));
    saveNameInput.value = null;
    refreshLoadList();
  });

  // Load button
  document.getElementById("load").addEventListener("click", () => {
    const loadList = document.getElementById("loadList") as HTMLSelectElement;
    if (loadList.children.length <= 1) {
      return;
    } // Must have some saves (first is placeholder)
    const saves = getSaves();
    const optionValue = (loadList.children[
      loadList.selectedIndex
    ] as HTMLOptionElement).value;
    if (optionValue.length == 0) {
      return;
    } // Must have a name
    // console.log(optionValue, typeof(optionValue))
    const saveObj = JSON.parse(optionValue) as Object;
    (document.getElementById("units") as HTMLSelectElement).selectedIndex =
      saveObj["units"];
    (document.getElementById("topLeft") as HTMLSelectElement).selectedIndex =
      saveObj["topLeft"];
    (document.getElementById("drawHorz") as HTMLInputElement).checked =
      saveObj["drawHorz"];
    (document.getElementById("flip") as HTMLInputElement).checked =
      saveObj["flip"];
    INPUTS.map((input, idx) => {
      input.value = saveObj["values"][idx];
    });
    loadList.selectedIndex = 0;
    resolveInputs();
  });

  draw = SVG().addTo("#drawing").size("100%", "100%");

  resolveInputs();
  refreshLoadList();

  drawExample();
});
